// src/components/crm/NoteMediaLightbox.js
// PHASE 1.7 VERIFIER: r1-17a-web-lb-3c8f4e2a
//
// Full-screen photo viewer for the Notes & Media grid in NoteCard.
//
// Behavior:
//   - Opens at a specific index (the photo the user tapped)
//   - ESC key closes
//   - Left / Right arrow keys navigate when there are multiple photos
//   - Click outside the image (the backdrop) closes
//   - Click the X button closes
//   - Image loads via getDownloadURL() from Firebase Storage
//   - Spinner while the URL resolves; falls back to thumb if full-size fails
//
// ── Defense-in-depth notes ────────────────────────────────────────────────
//   1. Storage rules gate getDownloadURL() to org members — a non-member
//      who somehow had a storagePath could not actually fetch the bytes.
//   2. No download button, no zoom, no annotation — those are Phase 1.10.
//   3. ESC + arrow listeners are scoped to mount; cleaned up on close.
//   4. Body scroll is locked while the lightbox is open so background
//      content doesn't jiggle behind the overlay.

import React, { useEffect, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../../firebase/config';

const NoteMediaLightbox = ({ media, initialIndex = 0, onClose }) => {
  const [index, setIndex] = useState(initialIndex);
  const [fullUrl, setFullUrl] = useState(null);
  const [thumbUrl, setThumbUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const total = Array.isArray(media) ? media.length : 0;
  const current = total > 0 ? media[index] : null;

  // --- Navigation ---
  const goPrev = useCallback(() => {
    if (total <= 1) return;
    setIndex((i) => (i - 1 + total) % total);
  }, [total]);

  const goNext = useCallback(() => {
    if (total <= 1) return;
    setIndex((i) => (i + 1) % total);
  }, [total]);

  // --- Keyboard handling ---
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext, onClose]);

  // --- Lock body scroll while open ---
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // --- Resolve download URLs for current image ---
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setFullUrl(null);
    setThumbUrl(null);

    if (!current?.storagePath) {
      setLoading(false);
      setError(true);
      return undefined;
    }

    // Start fetching the full-size URL. In parallel, fetch the thumb URL
    // (if available) so we can show it instantly while the full image
    // downloads in the browser.
    const fullPromise = getDownloadURL(ref(storage, current.storagePath))
      .then((url) => { if (!cancelled) setFullUrl(url); })
      .catch((err) => {
        console.warn('NoteMediaLightbox: full URL failed', err);
        if (!cancelled) setError(true);
      });

    if (current.thumbnailPath) {
      getDownloadURL(ref(storage, current.thumbnailPath))
        .then((url) => { if (!cancelled) setThumbUrl(url); })
        .catch(() => { /* thumb is optional; ignore */ });
    }

    fullPromise.finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [current?.storagePath, current?.thumbnailPath]);

  if (!current) return null;

  const handleBackdropClick = (e) => {
    // Only close if the click is on the backdrop itself, not bubbled from
    // the image or controls.
    if (e.target === e.currentTarget) onClose?.();
  };

  // Show the thumb under the full-size while the full image loads — the
  // browser caches whichever lands first and the user sees something fast.
  const displayUrl = fullUrl || thumbUrl;

  return (
    <div
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
    >
      {/* Close button — top right */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 flex items-center justify-center w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
        title="Close (Esc)"
      >
        <X size={22} />
      </button>

      {/* Counter — top left, only when multiple */}
      {total > 1 && (
        <div className="absolute top-4 left-4 text-white/80 text-sm font-medium z-10">
          {index + 1} / {total}
        </div>
      )}

      {/* Prev / Next — only when multiple */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-4 flex items-center justify-center w-12 h-12 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
            title="Previous (←)"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-4 flex items-center justify-center w-12 h-12 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
            title="Next (→)"
          >
            <ChevronRight size={28} />
          </button>
        </>
      )}

      {/* Main image area */}
      <div className="w-full h-full flex items-center justify-center p-4 sm:p-12">
        {loading && !displayUrl && (
          <Loader2 size={36} className="text-white/60 animate-spin" />
        )}
        {error && !displayUrl && (
          <p className="text-white/70 text-sm">Photo could not be loaded.</p>
        )}
        {displayUrl && (
          <img
            src={displayUrl}
            alt=""
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>
    </div>
  );
};

export default NoteMediaLightbox;